import { registerHubServiceWorker } from './push'

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

declare global {
  interface Window {
    __bpDeferredInstallPrompt?: BeforeInstallPromptEvent | null
  }
}

let capturing = false

export function captureInstallPromptEarly(): void {
  if (typeof window === 'undefined' || capturing) return
  capturing = true
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    window.__bpDeferredInstallPrompt = e as BeforeInstallPromptEvent
  })
  window.addEventListener('appinstalled', () => {
    window.__bpDeferredInstallPrompt = null
  })
}

export function isIosDevice(): boolean {
  const ua = navigator.userAgent || ''
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

export function isSamsungInternet(): boolean {
  return /SamsungBrowser/i.test(navigator.userAgent || '')
}

export function isStandaloneApp(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

export function isInAppBrowser(): boolean {
  const ua = (navigator.userAgent || '').toLowerCase()
  return (
    /fbav|fban|instagram|line\/|twitter|linkedinapp|whatsapp/.test(ua) ||
    /; wv\)/.test(ua) ||
    (ua.includes('gsa/') && isIosDevice())
  )
}

export function shouldShowInstallOptin(): boolean {
  if (isStandaloneApp()) return false
  return true
}

function hasDeferredPrompt(): boolean {
  const ev = window.__bpDeferredInstallPrompt
  return Boolean(ev && typeof ev.prompt === 'function')
}

function installGuideUrl(campanhaId?: number): string {
  if (campanhaId && campanhaId > 0) {
    return `/instalar-app?c=${campanhaId}`
  }
  return '/instalar-app'
}

/** Espelha o hub clássico: BIP costuma chegar só após o SW ficar ready. */
async function waitForNativePrompt(ms = 3200): Promise<boolean> {
  if (hasDeferredPrompt()) return true

  captureInstallPromptEarly()
  try {
    await registerHubServiceWorker()
  } catch {
    /* ignore */
  }

  return new Promise((resolve) => {
    let finished = false
    const done = (ok: boolean) => {
      if (finished) return
      finished = true
      window.removeEventListener('beforeinstallprompt', onPrompt)
      resolve(ok)
    }
    const onPrompt = () => done(true)

    window.addEventListener('beforeinstallprompt', onPrompt)
    window.setTimeout(() => done(hasDeferredPrompt()), ms)

    if ('serviceWorker' in navigator) {
      void navigator.serviceWorker.ready
        .then(() => {
          window.setTimeout(() => {
            if (hasDeferredPrompt()) done(true)
          }, 500)
        })
        .catch(() => {
          /* ignore */
        })
    }
  })
}

export type PromptInstallOpts = {
  /** Se o Chrome não oferecer o prompt, redireciona ao guia com branding da campanha. */
  campanhaId?: number
  /** false = só tenta prompt nativo (sem redirecionar). */
  fallbackGuide?: boolean
}

export async function promptInstallApp(
  opts: PromptInstallOpts = {},
): Promise<'accepted' | 'dismissed' | 'guide' | 'unavailable'> {
  const fallbackGuide = opts.fallbackGuide !== false

  if (isIosDevice()) {
    window.location.href = installGuideUrl(opts.campanhaId)
    return 'guide'
  }
  if (isInAppBrowser()) {
    throw new Error('Abra no Chrome (menu ⋮ → Abrir no Chrome) para instalar o app.')
  }

  if (!hasDeferredPrompt()) {
    await waitForNativePrompt(3200)
  }

  const ev = window.__bpDeferredInstallPrompt
  if (ev && typeof ev.prompt === 'function') {
    window.__bpDeferredInstallPrompt = null
    await ev.prompt()
    const choice = await ev.userChoice
    return choice.outcome
  }

  if (fallbackGuide) {
    window.location.href = installGuideUrl(opts.campanhaId)
    return 'guide'
  }

  if (isSamsungInternet()) {
    throw new Error(
      'No Internet da Samsung: ⋮ na barra de baixo → Adicionar página à → Tela inicial. No Chrome o botão abre o instalador nativo.',
    )
  }

  throw new Error(
    'No Chrome, use ⋮ → Instalar app. Se a opção não aparecer, recarregue a página e tente de novo.',
  )
}
